import React, { useMemo, useState, useCallback } from "react";
import { scaleSqrt, scaleOrdinal } from "d3-scale";
import { schemeSet2 } from "d3-scale-chromatic";
import { rgb } from "d3-color";
import { forceSimulation, forceCollide, forceX, forceY } from "d3-force";

export default function ServicePackedBubbleChart({
                                                     data = [],
                                                     width = 800,
                                                     height = 600,
                                                     topN = 60,
                                                     minRadius = 20,
                                                     maxRadius = 100
                                                 }) {
    // Add padding to prevent edge clipping
    const padding = maxRadius; // Extra space for largest bubbles

    const pruned = useMemo(
        () =>
            [...(data ?? [])]
                .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                .slice(0, topN),
        [data, topN]
    );

    const color = useMemo(() => scaleOrdinal(schemeSet2), []);

    // Dynamically scale maxRadius so all bubbles fill ~85% of the chart area.
    // Uses the median count so wide/skewed datasets size the typical bubble well,
    // rather than letting a few outliers dictate the scale.
    const effectiveMaxRadius = useMemo(() => {
        if (!pruned.length) return maxRadius;
        // pruned is sorted descending by count
        const maxCount = pruned[0].count || 1;
        const minCount = pruned[pruned.length - 1].count || 0;
        const medianCount = pruned[Math.floor(pruned.length / 2)].count || 1;

        // Target: if every bubble were the median radius, they'd fill ~85% of the area
        const medianTargetR = Math.sqrt((width * height * 0.85) / (Math.PI * pruned.length));

        // Where does the median sit on the sqrt scale (0–1)?
        const t = maxCount > minCount
            ? Math.sqrt((medianCount - minCount) / (maxCount - minCount))
            : 1;

        // Solve: medianTargetR = minRadius + t * (effectiveMax - minRadius)
        const derived = t > 0 ? (medianTargetR - minRadius) / t + minRadius : maxRadius;

        return Math.min(maxRadius, Math.max(minRadius + 5, derived));
    }, [pruned, width, height, maxRadius, minRadius]);

    // Scale for bubble radius
    const radiusScale = useMemo(() => {
        const counts = pruned.map((d) => d.count || 0);
        const min = Math.min(...counts);
        const max = Math.max(...counts);
        return scaleSqrt().domain([min, max]).range([minRadius, effectiveMaxRadius]);
    }, [pruned, minRadius, effectiveMaxRadius]);

    // Compute bubble positions with d3-force to prevent overlap
    const bubbles = useMemo(() => {
        const nodes = pruned.map((d) => ({
            ...d,
            r: radiusScale(d.count),
            x: Math.random() * width,
            y: Math.random() * height
        }));

        const simulation = forceSimulation(nodes)
            .force("x", forceX(width / 2).strength(0.05))
            .force("y", forceY(height / 2).strength(0.05))
            .force("collide", forceCollide((d) => d.r + 2))
            // Add boundary forces to keep bubbles within padded bounds
            .force("boundX", forceX().x((d) => Math.max(padding, Math.min(width - padding, d.x))).strength(0.1))
            .force("boundY", forceY().y((d) => Math.max(padding, Math.min(height - padding, d.y))).strength(0.1))
            .stop();

        for (let i = 0; i < 300; i++) simulation.tick();

        // Hard-clamp any stragglers that escaped the boundary forces.
        nodes.forEach((d) => {
            d.x = Math.max(d.r + 2, Math.min(width - d.r - 2, d.x));
            d.y = Math.max(d.r + 2, Math.min(height - d.r - 2, d.y));
        });

        return nodes;
    }, [pruned, radiusScale, width, height, padding]);

    const [tooltip, setTooltip] = useState(null); // { bubble, x, y }

    const handleMouseEnter = useCallback((d, e) => {
        setTooltip({ bubble: d, x: e.clientX, y: e.clientY });
    }, []);

    const handleMouseMove = useCallback((e) => {
        setTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTooltip(null);
    }, []);

    const total = useMemo(() => bubbles.reduce((s, d) => s + (d.count || 0), 0), [bubbles]);

    const labelColor = (fill) => {
        const c = rgb(fill);
        const L =
            0.2126 * (c.r / 255) +
            0.7152 * (c.g / 255) +
            0.0722 * (c.b / 255);
        return L < 0.55 ? "#fff" : "#111";
    };

    if (!data || !data.length) return null;

    // Build legend items from pruned dataset
    const legendItems = pruned.map((d) => ({
        name: d.service,
        color: color(d.service)
    }));

    return (
        <div style={{ display: "flex", flexDirection: "row", width: "100%", height: "100%", minHeight: 0 }}>
            {/* Chart fills available space with padding to prevent edge clipping */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0
                }}
            >
            <svg
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
                style={{ display: "block" }}
            >
                {bubbles.map((d, i) => {
                    const fill = color(d.service);
                    const textColor = labelColor(fill);

                    const name = d.service;
                    const count = d.count?.toLocaleString();

                    const titleSize = Math.min(18, Math.max(10, d.r * 0.22));
                    const countSize = Math.min(14, Math.max(9, d.r * 0.16));

                    const shortName = name.length > 18 ? name.slice(0, 18) + "…" : name;

                    const showLabel = d.r >= 50;   // name + count
                    const showCount = d.r >= 30;   // count only

                    return (
                        <g
                            key={i}
                            transform={`translate(${d.x},${d.y})`}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={(e) => handleMouseEnter(d, e)}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        >
                            <circle r={d.r} fill={fill} />

                            {showLabel && (
                                <text
                                    textAnchor="middle"
                                    fill={textColor}
                                    style={{ pointerEvents: "none" }}
                                >
                                    <tspan x="0" y={-4} fontSize={titleSize}>
                                        {shortName}
                                    </tspan>
                                    <tspan x="0" dy={countSize + 4} fontSize={countSize} fontWeight="700">
                                        {count}
                                    </tspan>
                                </text>
                            )}

                            {!showLabel && showCount && (
                                <text
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill={textColor}
                                    fontSize={countSize}
                                    fontWeight="700"
                                    style={{ pointerEvents: "none" }}
                                >
                                    {count}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            </div>

            {/* Color Legend - fixed on the right, same height as chart */}
            <div
                style={{
                    flexShrink: 0,
                    width: "200px",
                    marginLeft: "20px",
                    minHeight: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto"
                }}
            >
                {legendItems.map((item, i) => (
                    <div
                        key={i}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "6px"
                        }}
                    >
                        <div
                            style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: item.color,
                                marginRight: "8px"
                            }}
                        />
                        <span style={{ fontSize: "12px" }}>{item.name}</span>
                    </div>
                ))}
            </div>

            {tooltip && (
                <div style={{
                    position: "fixed",
                    left: tooltip.x + 14,
                    top: tooltip.y - 10,
                    backgroundColor: "#fff",
                    border: "1px solid #d3d3d3",
                    borderRadius: "4px",
                    padding: "8px 12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                    pointerEvents: "none",
                    zIndex: 9999,
                    minWidth: "140px"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "6px",
                        gap: "8px"
                    }}>
                        <div style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: color(tooltip.bubble.service),
                            flexShrink: 0
                        }} />
                        <span style={{ fontWeight: "700", fontSize: "13px" }}>
                            {tooltip.bubble.service}
                        </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#444" }}>
                        <span style={{ fontWeight: "600" }}>
                            {tooltip.bubble.count?.toLocaleString()}
                        </span>
                        {" companies"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                        {total > 0
                            ? ((tooltip.bubble.count / total) * 100).toFixed(1) + "% of total"
                            : null}
                    </div>
                </div>
            )}
        </div>
    );
}
